import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Download, Info, ChevronDown, ChevronRight, Search, AlertTriangle, Check, X,
} from 'lucide-react';

/**
 * High5 — Marka Arama Kapsaması.
 *
 * 🔑🔑 Kapsama = GÖSTERİM ÷ marka talebi. Tıklama ÷ hacim okuması KALDIRILDI:
 *    aranma hacmi bir gösterim fırsatı sayısı, tıklama ise gösterimin altındaki
 *    ayrı bir olay — ikisinin oranı bir pay ölçüsü değil. Tıklama tabloda ham
 *    metrik ve CTR olarak duruyor.
 *
 * 🔑🔑 Payda tek terim değil, markanın KELİME EVRENİ ("hoka" 27.100 ama
 *    "hoka ayakkabı" 64.100, "hoka bondi 9" 42.400... toplam 169.130).
 *
 * 🔑 Kapsama %100'ü aşarsa: kampanya marka evreninin dışından gösterim topluyor
 *    (ON'un "on" ifadesi gibi) ya da talep, Keyword Planner'ın yayımladığı uzun
 *    kuyruktan daha geniş. Google'ın kendi gösterim payı ayrı sütunda duruyor.
 */

const API_BASE = 'https://api.glowytics.com';
const BRAND_COLOR = '#0f766e';

const fmtInt = (n) => (n == null ? '—' : Math.round(Number(n)).toLocaleString('tr-TR'));
const fmtMoney = (n) => (n == null ? '—' : `${Math.round(Number(n)).toLocaleString('tr-TR')} ₺`);
const fmtMoney2 = (n) => (n == null ? '—' : `${Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`);
const fmtPct = (n, d = 1) => (n == null ? '—' : `%${(Number(n) * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`);

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const monthLabel = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return `${MONTHS_TR[m - 1]} ${y}`;
};

/** Son 24 ayın YYYY-MM listesi (bugünden geriye). */
function monthOptions(count = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

/** Google'ın kendi gösterim payı — %60+ iyi, %30 altı zayıf. */
const shareTone = (v) => {
  if (v == null) return 'text-gray-300';
  if (v >= 0.6) return 'text-emerald-700';
  if (v >= 0.3) return 'text-amber-600';
  return 'text-red-600';
};

/** Kapsama tonu. %120 üstü "iyi" değil, "evren dışına taşmış" demek. */
const coverageTone = (v) => {
  if (v == null) return 'text-gray-300';
  if (v > 1.2) return 'text-violet-700';
  if (v >= 0.6) return 'text-emerald-700';
  if (v >= 0.3) return 'text-amber-600';
  return 'text-red-600';
};

/**
 * Aylık gösterim payı hücresi. Renk = değerin kendisi; boş ay (kampanya o ay
 * yayında değil) gri tire — %0 ile karıştırılmamalı.
 */
function ShareCell({ cell }) {
  if (!cell || cell.impressionShare == null) {
    return <td className="px-2 py-1.5 text-center text-gray-300">—</td>;
  }
  const v = cell.impressionShare;
  // Tek renk (marka yeşili) üzerinde doygunluk: %0 beyaz, %100 dolu.
  const bg = `rgba(15, 118, 110, ${(0.06 + Math.min(1, v) * 0.55).toFixed(3)})`;
  return (
    <td
      className={`px-2 py-1.5 text-center tabular-nums font-semibold ${v >= 0.55 ? 'text-white' : 'text-gray-800'}`}
      style={{ background: bg }}
      title={`${fmtInt(cell.impressions)} gösterim · sıra kaybı ${fmtPct(cell.rankLost)} · bütçe kaybı ${fmtPct(cell.budgetLost)}`}
    >
      {Math.round(v * 100)}
    </td>
  );
}

function Kpi({ title, value, sub, tone }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${tone || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

/** 0-100 arası tek çubuk; %100'ü aşan değer çubuğu doldurur, sayı gerçeği söyler. */
function Bar({ value, tone }) {
  if (value == null) return <span className="text-gray-300">—</span>;
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="h-1.5 w-14 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: value > 1.2 ? '#7c3aed' : BRAND_COLOR }} />
      </div>
      <span className={`tabular-nums font-semibold ${tone(value)}`}>{fmtPct(value)}</span>
    </div>
  );
}

const SORTS = {
  brandVolume: (a, b) => (b.brandVolume || 0) - (a.brandVolume || 0),
  impressions: (a, b) => b.impressions - a.impressions,
  clicks: (a, b) => b.clicks - a.clicks,
  cost: (a, b) => b.cost - a.cost,
  impressionShare: (a, b) => (b.impressionShare || 0) - (a.impressionShare || 0),
  coverage: (a, b) => (b.coverage || 0) - (a.coverage || 0),
  keywordCoverage: (a, b) => (b.keywordCoverage || 0) - (a.keywordCoverage || 0),
};

export default function High5BrandSearchCoverage() {
  const { selectedBrand } = useAuth();
  const [adsMonth, setAdsMonth] = useState('2026-08');
  const [volumeMonth, setVolumeMonth] = useState('2026-07');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [sortKey, setSortKey] = useState('brandVolume');

  const months = useMemo(() => monthOptions(24), []);

  const fetchData = async (am = adsMonth, vm = volumeMonth, refresh = false) => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(
        `${API_BASE}/api/high5-brand-coverage?adsMonth=${am}&volumeMonth=${vm}${refresh ? '&refresh=1' : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
        },
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Veri alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort(SORTS[sortKey] || SORTS.brandVolume);
  }, [data, sortKey]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const head = [
      'Marka', 'Tepe terim', `Tepe terim hacmi (${data.volumeMonth})`,
      `Marka talebi (${data.volumeMonth})`, 'Evrendeki kelime sayısı', 'Kelimelerimizin kapsadığı talep',
      `${data.adsMonth} gösterim`, `${data.adsMonth} tıklama`, `${data.adsMonth} maliyet`,
      'CTR', 'TBM', 'Gösterim payı', 'Sıra kaybı', 'Bütçe kaybı',
      'Kapsama (gösterim/talep)', 'Kelime kapsaması', 'Kampanya',
    ];
    const lines = [head.join(';')];
    for (const r of rows) {
      lines.push([
        r.label, r.headKeyword || '', r.headVolume ?? '',
        r.brandVolume ?? '', r.brandTermCount ?? '', r.coveredVolume ?? '',
        r.impressions, r.clicks, Math.round(r.cost),
        r.ctr != null ? (r.ctr * 100).toFixed(2) : '', r.cpc != null ? r.cpc.toFixed(2) : '',
        r.impressionShare != null ? (r.impressionShare * 100).toFixed(1) : '',
        r.rankLost != null ? (r.rankLost * 100).toFixed(1) : '',
        r.budgetLost != null ? (r.budgetLost * 100).toFixed(1) : '',
        r.coverage != null ? (r.coverage * 100).toFixed(1) : '',
        r.keywordCoverage != null ? (r.keywordCoverage * 100).toFixed(1) : '',
        r.campaignName,
      ].join(';'));
    }
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `high5_marka_kapsama_${data.volumeMonth}_${data.adsMonth}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const t = data?.totals;
  const Th = ({ k, children, className = '' }) => (
    <th
      className={`px-3 py-2 text-right font-semibold cursor-pointer select-none hover:text-gray-900 ${sortKey === k ? 'text-gray-900' : 'text-gray-500'} ${className}`}
      onClick={() => k && setSortKey(k)}
    >
      {children}{sortKey === k ? ' ↓' : ''}
    </th>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      {/* Başlık + ay seçiciler */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Search className="w-6 h-6" style={{ color: BRAND_COLOR }} />
            Marka Arama Kapsaması
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Markanın <strong>{monthLabel(data?.volumeMonth || volumeMonth)}</strong> arama talebi ↔ marka arama
            kampanyasından <strong>{monthLabel(data?.adsMonth || adsMonth)}</strong> aldığımız gösterim.
            Kapsama = gösterim ÷ talep.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-500">
            <span className="block mb-1">Talep ayı</span>
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              value={volumeMonth}
              onChange={(e) => { setVolumeMonth(e.target.value); fetchData(adsMonth, e.target.value); }}
            >
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </label>
          <label className="text-xs text-gray-500">
            <span className="block mb-1">Reklam ayı</span>
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
              value={adsMonth}
              onChange={(e) => { setAdsMonth(e.target.value); fetchData(e.target.value, volumeMonth); }}
            >
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </label>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={() => fetchData(adsMonth, volumeMonth, true)}
            disabled={loading}
            title="Kelime evrenlerini Keyword Planner'dan yeniden çeker (7 günlük önbelleği atlar, ~30 sn)."
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Evreni tazele
          </button>
          <button
            onClick={exportCsv}
            disabled={!data?.rows?.length}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {data?.keywordPlannerError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Aranma hacmi çekilemedi: {data.keywordPlannerError} — tablodaki reklam sayıları doğru, talep ve kapsama sütunları boş.</span>
        </div>
      )}
      {!!data?.universeErrors?.length && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {data.universeErrors.map((u) => u.brand).join(', ')} için kelime evreni çekilemedi (Keyword Planner kotası).
            O satırlarda kapsama boş — “Evreni tazele” ile tekrar deneyebilirsiniz.
          </span>
        </div>
      )}

      {data?.latestVolumeMonth && data.volumeMonth > data.latestVolumeMonth && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Keyword Planner {monthLabel(data.volumeMonth)} hacmini <strong>henüz yayımlamadı</strong> — son yayımlanan ay{' '}
            {monthLabel(data.latestVolumeMonth)}. Talep ve kapsama sütunları bu yüzden boş.
          </span>
        </div>
      )}

      {/* Neyin ne olduğunu tek yerde açıklayan şerit */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
        <div className="space-y-1">
          <p>
            <strong>Kapsama gösterim üzerinden hesaplanır.</strong> Aranma hacmi bir <em>gösterim fırsatı</em> sayısıdır:
            her arama, görünebileceğimiz bir açık artırmadır. Tıklama ise gösterimin altındaki ayrı bir olay (CTR), pay
            ölçüsü değil — bu yüzden “tıklama ÷ hacim” okuması kaldırıldı, tıklama tabloda ham metrik olarak duruyor.
          </p>
          <p>
            <strong>Payda tek terim değil, markanın kelime evreni.</strong> “hoka” tek başına 27.100 aranıyor ama
            “hoka ayakkabı” 64.100, “hoka bondi 9” 42.400… Her marka için Keyword Planner'dan, kampanyanın kendi
            kelimeleri tohum verilerek evren çıkarılıyor; aynı talebi tekrar yazan varyantlar (“new balance 530” /
            “new balance nb 530”) tek sayılıyor, diğer High5 markalarının adı geçen sorgular dışarıda bırakılıyor.
          </p>
          <p>
            <strong>Kapsama %100'ü aşabilir</strong> (mor renk): kampanya marka evreninin dışından gösterim alıyor demektir —
            ON kampanyası “on” ifadesine teklif veriyor, “on” Türkçe'de sayı sözcüğü. <strong>Gösterim payı</strong> sütunu
            ayrı bir şey ölçer: girdiğimiz açık artırmalardaki payımız (1 − sıra kaybı − bütçe kaybı).
          </p>
          <p>
            <strong>İki ay neden farklı:</strong> Keyword Planner en son yayımladığı ay
            {data?.latestVolumeMonth ? <> <strong>{monthLabel(data.latestVolumeMonth)}</strong></> : ''}; reklam tarafında
            son tam ay {monthLabel(data?.adsMonth || adsMonth)}. İki seçici bağımsız, aynı aya hizalayabilirsiniz.
          </p>
        </div>
      </div>

      {/* Özet */}
      {t && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi title={`${monthLabel(data.volumeMonth)} marka talebi`} value={fmtInt(t.brandVolume)} sub={`tepe terimler yalnız ${fmtInt(t.headVolume)}`} />
          <Kpi title={`${monthLabel(data.adsMonth)} gösterim`} value={fmtInt(t.impressions)} sub={`uygun havuz ${fmtInt(t.eligiblePool)}`} />
          <Kpi title="Kapsama" value={fmtPct(t.coverage)} sub="gösterim ÷ marka talebi" tone={coverageTone(t.coverage)} />
          <Kpi title="Kelime kapsaması" value={fmtPct(t.keywordCoverage)} sub="kelimelerimizin kapsadığı talep" />
          <Kpi title="Gösterim payı" value={fmtPct(t.impressionShare)} sub="Google ölçümü, gösterimle ağırlıklı" tone={shareTone(t.impressionShare)} />
          <Kpi title={`${monthLabel(data.adsMonth)} harcama`} value={fmtMoney(t.cost)} sub={`${fmtInt(t.clicks)} tıklama · CTR ${fmtPct(t.ctr, 2)} · TBM ${fmtMoney2(t.cpc)}`} />
        </div>
      )}

      {/* Ana tablo */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs">
                <th className="px-3 py-2 text-left font-semibold text-gray-500">Marka</th>
                <Th k="brandVolume">{monthLabel(data?.volumeMonth || volumeMonth)}<br />marka talebi</Th>
                <Th k="impressions">Gösterim</Th>
                <Th k="clicks">Tıklama</Th>
                <Th k="cost">Harcama</Th>
                <th className="px-3 py-2 text-right font-semibold text-gray-500">CTR</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-500">TBM</th>
                <Th k="impressionShare">Gösterim payı<br />(açık artırmada)</Th>
                <Th k="keywordCoverage">Kelime<br />kapsaması</Th>
                <Th k="coverage">Kapsama<br />gösterim ÷ talep</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const isOpen = !!open[r.campaignId];
                const uncoveredVolume = r.brandVolume != null && r.coveredVolume != null
                  ? r.brandVolume - r.coveredVolume : null;
                return (
                  <React.Fragment key={r.campaignId}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <button
                          className="flex items-center gap-1.5 font-semibold text-gray-900"
                          onClick={() => setOpen((o) => ({ ...o, [r.campaignId]: !o[r.campaignId] }))}
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                          {r.label}
                        </button>
                        <div className="pl-5 text-[11px] text-gray-400">
                          tepe terim <span className="text-gray-500">{r.headKeyword || '—'}</span>
                          {r.headVolume != null && <> · {fmtInt(r.headVolume)}</>}
                          {r.headKeyword && !r.headBid && (
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold"
                              title="Bu terime kampanyada teklif verilmiyor; kapsama düşük çıkması beklenir."
                            >
                              teklif yok
                            </span>
                          )}
                        </div>
                        {r.note && <div className="pl-5 text-[11px] text-amber-700 mt-0.5 max-w-[320px]">{r.note}</div>}
                        {r.universeError && (
                          <div className="pl-5 text-[11px] text-red-600 mt-0.5 max-w-[320px]">Kelime evreni çekilemedi.</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">
                        {fmtInt(r.brandVolume)}
                        {r.brandTermCount != null && (
                          <div className="text-[11px] font-normal text-gray-400">{fmtInt(r.brandTermCount)} kelime</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtInt(r.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.cost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtPct(r.ctr, 2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmtMoney2(r.cpc)}</td>
                      <td className="px-3 py-2">
                        <Bar value={r.impressionShare} tone={shareTone} />
                        <div className="text-[11px] text-gray-400 mt-0.5 text-right">
                          sıra kaybı {fmtPct(r.rankLost)} · bütçe {fmtPct(r.budgetLost)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                        {fmtPct(r.keywordCoverage)}
                        {uncoveredVolume > 0 && (
                          <div className="text-[11px] text-gray-400">{fmtInt(uncoveredVolume)} arama açıkta</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Bar value={r.coverage} tone={coverageTone} />
                        {r.coverage > 1.2 && (
                          <div className="text-[11px] text-violet-600 mt-0.5 text-right">evren dışından gösterim</div>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="bg-gray-50/60">
                        <td colSpan={10} className="px-6 py-4 space-y-4">
                          {/* Marka talep evreni */}
                          <div>
                            <div className="text-xs text-gray-500 mb-2">
                              <strong className="text-gray-700">Marka talebi — {monthLabel(data.volumeMonth)}.</strong>{' '}
                              {fmtInt(r.brandTermCount)} kelimede {fmtInt(r.brandVolume)} arama;
                              bunun {fmtInt(r.coveredVolume)} kadarı ({fmtPct(r.keywordCoverage)}) kampanya kelimelerimizin
                              kapsadığı sorgularda. {r.duplicatesMerged > 0 && <>Aynı talebi tekrar yazan {fmtInt(r.duplicatesMerged)} varyant tekilleştirildi. </>}
                              {r.universeTruncated && <span className="text-amber-700">Liste Keyword Planner tavanına dayandı, uzun kuyruk eksik olabilir. </span>}
                              En çok arananlar:
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="px-2 py-1 text-left font-semibold">Sorgu</th>
                                    <th className="px-2 py-1 text-right font-semibold">{monthLabel(data.volumeMonth)} arama</th>
                                    <th className="px-2 py-1 text-left font-semibold">Kelimelerimiz kapsıyor mu</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {(r.brandTerms || []).map((k) => (
                                    <tr key={k.text}>
                                      <td className="px-2 py-1 text-gray-800">{k.text}</td>
                                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(k.volume)}</td>
                                      <td className="px-2 py-1">
                                        {k.covered
                                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> kapsıyor</span>
                                          : <span className="inline-flex items-center gap-1 text-red-600"><X className="w-3 h-3" /> açıkta</span>}
                                      </td>
                                    </tr>
                                  ))}
                                  {!r.brandTerms?.length && (
                                    <tr><td colSpan={3} className="px-2 py-3 text-gray-400">Kelime evreni yok.</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Kampanya kelimeleri */}
                          <div>
                            <div className="text-xs text-gray-500 mb-2">
                              <strong className="text-gray-700">Kampanya kelimelerimiz — {monthLabel(data.adsMonth)}.</strong>
                              {r.headCpcLow != null && (
                                <> Tepe terim rekabeti <strong>{r.headCompetition || '—'}</strong>, sayfa üstü teklif aralığı{' '}
                                  {fmtMoney2(r.headCpcLow)} – {fmtMoney2(r.headCpcHigh)}.</>
                              )}
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-xs">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="px-2 py-1 text-left font-semibold">Kelime</th>
                                    <th className="px-2 py-1 text-left font-semibold">Eşleşme</th>
                                    <th className="px-2 py-1 text-right font-semibold">Gösterim</th>
                                    <th className="px-2 py-1 text-right font-semibold">Tıklama</th>
                                    <th className="px-2 py-1 text-right font-semibold">Harcama</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {(r.keywords || []).map((k) => (
                                    <tr key={k.text}>
                                      <td className="px-2 py-1 text-gray-800">{k.text}</td>
                                      <td className="px-2 py-1 text-gray-400">{k.matchType}</td>
                                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(k.impressions)}</td>
                                      <td className="px-2 py-1 text-right tabular-nums">{fmtInt(k.clicks)}</td>
                                      <td className="px-2 py-1 text-right tabular-nums">{fmtMoney(k.cost)}</td>
                                    </tr>
                                  ))}
                                  {!r.keywords?.length && (
                                    <tr><td colSpan={5} className="px-2 py-3 text-gray-400">Bu ayda kampanya kelimesi verisi yok.</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!rows.length && !loading && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">Bu ayda marka arama kampanyası verisi yok.</td></tr>
              )}
              {!rows.length && loading && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-gray-400">
                  Marka kelime evrenleri Keyword Planner'dan çekiliyor — ilk açılış 20-40 saniye sürebilir.
                </td></tr>
              )}
            </tbody>
            {t && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
                <tr>
                  <td className="px-3 py-2">Toplam <span className="text-gray-400 font-normal">· {rows.length} marka</span></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.brandVolume)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.impressions)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtInt(t.clicks)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(t.cost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(t.ctr, 2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney2(t.cpc)}</td>
                  <td className="px-3 py-2"><Bar value={t.impressionShare} tone={shareTone} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtPct(t.keywordCoverage)}</td>
                  <td className="px-3 py-2"><Bar value={t.coverage} tone={coverageTone} /></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Aylık gösterim payı — tek metrik, sütunlar ay */}
      {!!data?.shareMonths?.length && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Marka arama kampanyaları — aylık gösterim payı</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Her markanın <strong>GG/CO/SEARCHBR</strong> kampanyasının aylık gösterim payı (%), Google'ın kendi
              ölçümü: 1 − sıra kaybı − bütçe kaybı, gün gün ortalama değil <em>havuz üzerinden</em> ağırlıklı.
              Boş hücre = o ay kampanya yayında değil, %0 değil. Hücrenin üstünde bekleyince gösterim ve kayıp
              kırılımı çıkar. Kampanya yeniden kurulsa bile satır marka adına göre sürüyor.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 sticky left-0 bg-gray-50">Marka</th>
                  {data.shareMonths.map((m) => (
                    <th key={m} className="px-2 py-2 text-center font-semibold text-gray-500 whitespace-nowrap">
                      {monthLabel(m).replace(' ', '\u00a0')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.campaignId}>
                    <td className="px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white">{r.label}</td>
                    {data.shareMonths.map((m) => <ShareCell key={m} cell={r.monthlyShare?.[m]} />)}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td className="px-3 py-1.5 font-semibold text-gray-700 sticky left-0 bg-gray-50">Toplam</td>
                  {data.shareMonths.map((m) => <ShareCell key={m} cell={data.shareTotals?.[m]} />)}
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
